import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { createPatientApi } from "../../../api-helpers/PatientApiHelper";
import { createCommonUtilsApi } from "../../../api-helpers/CommonUtilsApiHelper";
import Lookups from "../../../constants/Lookups";
import { Lookup } from "common-util-experience-client";
import { formateDate } from "../../schedule/dateFormat";

const MEDICATION_ROW_LIMIT = 4;

interface SummaryMedicationCardProps {
  patientId?: string;
  variant?: "compact-list" | "care-record";
}

export const SummaryMedicationCard = ({
  patientId: propPatientId,
  variant = "compact-list",
}: SummaryMedicationCardProps = {}) => {
  const [medications, setMedications] = useState<any>({ data: [], total: 0 });
  const [loading, setLoading] = useState<boolean>(true);

  const { patientId: routePatientId } = useParams<{ patientId: string }>();
  const patientId = propPatientId || routePatientId;
  const patientApi = createPatientApi();
  const lookupApi = createCommonUtilsApi();

  const getActiveMedicationStatus = async (): Promise<string[]> => {
    let response, medicationStatus: Lookup[] = [];
    try {
      response = await lookupApi.lookupControllerFindAll(Lookups.MEDICATION_STATUS);
      medicationStatus = response.data.filter((lookup: Lookup) => lookup.name?.toLowerCase() === "active");
    } catch (error) {
      console.log(error);
    }
    return medicationStatus.length > 0 ? [medicationStatus[0].id] : [];
  };

  const getMedications = async () => {
    try {
      const activeMedicationStatusId = await getActiveMedicationStatus();
      const medicationsPayload = {
        filters: [
          {
            propertyName: "patientId",
            operator: "equals",
            propertyValue: patientId,
          },
          {
            propertyName: "status",
            operator: "in",
            propertyValue: activeMedicationStatusId,
            updateFilter: true,
          },
        ],
        sortCriteria: [{ propertyName: "medicationStartDate", descending: true }],
        startRow: 0,
        endRow: 0,
      };
      const response = await patientApi.patientsControllerGetMedicationGridData(medicationsPayload);
      setMedications(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getMedications();
  }, []);

  if (variant === "care-record") {
    // design_handoff_patient_summary (option 2a): "Care record" band.
    if (loading) return null;
    if (medications.total === 0) {
      return <span className="ps-empty">No medications recorded</span>;
    }
    return (
      <div className="ps-record-list">
        {medications.data.slice(0, MEDICATION_ROW_LIMIT).map((medication: any) => (
          <div className="ps-record-row" key={medication.id}>
            <span className="ps-record-row__date">
              {formateDate(medication.medicationStartDate, "UTC")}
            </span>
            <span className="ps-record-row__desc">{medication.medicationName}</span>
            <span />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="patient-summary-compact-card__body">
      {loading ? (
        "Loading..."
      ) : medications.total > 0 ? (
        <div className="patient-summary-row-list">
          {medications.data.slice(0, MEDICATION_ROW_LIMIT).map((medication: any) => (
            <div className="patient-summary-row-list__row" key={medication.id}>
              <span className="patient-summary-row-list__label">
                {medication.medicationName}
              </span>
              <span className="patient-summary-row-list__meta">
                {formateDate(medication.medicationStartDate, "UTC")}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <span className="patient-summary-empty-badge">No data</span>
      )}
    </div>
  );
};
